import _ from 'lodash';
import { BaseSearchStore } from '@/search/baseSearch.store';
import { TABLE_DEFINITION_SUBTYPES } from '@/search/search.constants';
import { hasInvalidSearchTypes } from '@/search/search.utilities';
import type { PersistenceLevel } from '@/core/flux.service';
import type { BaseSearchState } from '@/search/baseSearch.store';
import type { SearchPanes } from '@/search/search.constants';
import type { SearchBreadcrumb } from '@/search/search.types';

interface ToggleSelectAllPayload {
  items: Array<{ id: string }>;
}

interface ToggleSelectedItemPayload {
  itemId: string;
  items?: Array<{ id: string }>;
}

interface SetSelectAllPayload {
  isAllSelected: boolean;
}

interface SearchPersistedBreadcrumb {
  id: string;
  name?: string;
  type?: string;
  isScalingTableCSVFlow?: boolean;
}

interface SearchPersistedItem {
  id: string;
}

export type SearchDehydratedState = Pick<
  BaseSearchState,
  | 'nameFilter'
  | 'isExactName'
  | 'searchLocallyScoped'
  | 'descriptionFilter'
  | 'typeFilter'
  | 'datasourceFilter'
  | 'sortBy'
  | 'isAdvancedMode'
  | 'mode'
  | 'currentAsset'
  | 'selectAllChecked'
  | 'selectedItemIds'
  | 'selectedItemTable'
  | 'selectedHierarchy'
  | 'selectedScalingTable'
  | 'selectedScalingTableRow'
  | 'currentHierarchyPath'
  | 'recentDatasourceFilters'
  | 'isUsingDatasourcePrefsSearchFilters'
> & {
  breadcrumbs?: SearchPersistedBreadcrumb[];
  items?: SearchPersistedItem[];
};

export class SearchStore extends BaseSearchStore {
  pane: SearchPanes = 'main';
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqSearchStore';

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} The state that is serialized
   */
  dehydrate(): SearchDehydratedState {
    const state: SearchDehydratedState = {
      nameFilter: this.nameFilter,
      isExactName: this.isExactName,
      searchLocallyScoped: this.searchLocallyScoped,
      descriptionFilter: this.descriptionFilter,
      typeFilter: this.typeFilter,
      datasourceFilter: this.datasourceFilter,
      sortBy: this.sortBy,
      isAdvancedMode: this.isAdvancedMode,
      mode: this.mode,
      currentAsset: this.currentAsset,
      selectAllChecked: this.selectAllChecked,
      selectedItemIds: this.selectedItemIds,
      selectedItemTable: this.selectedItemTable,
      selectedHierarchy: this.selectedHierarchy,
      selectedScalingTable: this.selectedScalingTable,
      selectedScalingTableRow: this.selectedScalingTableRow,
      currentHierarchyPath: this.currentHierarchyPath,
      recentDatasourceFilters: this.recentDatasourceFilters,
      isUsingDatasourcePrefsSearchFilters: this.isUsingDatasourcePrefsSearchFilters,
    };

    if (state.mode === 'usages') {
      const persistedBreadcrumbs = this.breadcrumbs.map(({ id }: SearchBreadcrumb) => ({ id }));
      state.breadcrumbs = persistedBreadcrumbs;
    }
    if (hasInvalidSearchTypes(state.typeFilter)) {
      state.typeFilter = state.typeFilter?.filter((type: string) => !TABLE_DEFINITION_SUBTYPES.includes(type));
    }

    if (state.mode === 'scaling-tables-tree' || state.mode === 'item-hierarchies' || state.mode === 'hierarchy-nodes') {
      const persistedBreadcrumbs = this.breadcrumbs.map(
        ({ id, name, type, isScalingTableCSVFlow }: SearchBreadcrumb) => ({ id, name, type, isScalingTableCSVFlow }),
      );
      const persistedItems = this.items.map(({ id }) => ({ id })) as SearchPersistedItem[];
      state.breadcrumbs = persistedBreadcrumbs;
      state.items = persistedItems;
    }

    return state;
  }

  /**
   * Sets store from dehydratedState. If there are filters it fetches the results.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<SearchDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  handlers = {
    ...super.baseHandlers,
    SEARCH_TOGGLE_SELECT_ALL: ({ items }: ToggleSelectAllPayload) => {
      const selectAll = this.state.get('selectAllChecked');
      this.state.set('selectAllChecked', !selectAll);
      // add all items to selectedItemIds
      // if all was selected then we need to clear the items as we're toggling to off:
      const allItemIds = _.map(items, 'id');
      this.state.set('selectedItemIds', selectAll ? [] : allItemIds);
    },
    SEARCH_TOGGLE_SELECTED_ITEM: ({ itemId, items }: ToggleSelectedItemPayload) => {
      const selectedItemIds: string[] = [...this.state.get('selectedItemIds')];
      const selectAllChecked = this.state.get('selectAllChecked');
      const itemIdx = _.indexOf(selectedItemIds, itemId);
      if (itemIdx > -1) {
        // item currently selected - unselect it:
        _.pullAt(selectedItemIds, itemIdx);
        this.state.set('selectedItemIds', selectedItemIds);
        // if select all is checked we should unselect it as "all" are no longer selected:
        if (selectAllChecked) {
          this.state.set('selectAllChecked', undefined);
        }
      } else {
        const updatedSelectedItemIds = [...selectedItemIds, itemId];
        this.state.set('selectedItemIds', updatedSelectedItemIds);
        if (updatedSelectedItemIds.length === (items?.length ?? 0) && !selectAllChecked) {
          this.state.set('selectAllChecked', true);
        }
      }
    },
    SEARCH_CLEAR_SELECTED_ITEMS: () => {
      this.state.set('selectAllChecked', false);
      this.state.set('selectedItemIds', []);
    },
    SEARCH_SET_SELECT_ALL: ({ isAllSelected }: SetSelectAllPayload) => {
      this.state.set('selectAllChecked', isAllSelected);
    },
  };
}
